import { useState, useEffect, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { setLoading } from '../../../../store/actions';

import { KnowledgeBaseList } from './Components/KnowledgeBaseList';
import { KnowledgeBaseFormModal } from './Components/KnowledgeBaseFormModal';
import {
  getAllKnowledgeBases,
  createKnowledgeBase,
  updateKnowledgeBase,
  deleteKnowledgeBase,
  toggleKnowledgeBaseActive,
} from './Components/FormikHelpers/Api';

import './Components/knowledge-base.scss';

const CompanyKnowledgeBase = () => {
  const dispatch = useDispatch();

  const [entries, setEntries] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterActive, setFilterActive] = useState('');

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch { return null; }
  }, []);

  const fetchEntries = useCallback(async () => {
    try {
      dispatch(setLoading(true));
      const params = {};
      if (filterCity) params.city = filterCity;
      if (filterType) params.property_type = filterType;
      if (filterActive !== '') params.is_active = filterActive;
      const response = await getAllKnowledgeBases(params);
      setEntries(response?.data || []);
    } catch {
      setEntries([]);
      toast.error('Failed to load knowledge base entries');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, filterCity, filterType, filterActive]);

  useEffect(() => {
    const currentUser = getUserFromToken();
    setUser(currentUser);
  }, [getUserFromToken]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleCreate = () => {
    setEditingEntry(null);
    setIsModalOpen(true);
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setIsModalOpen(true);
  };

  const handleSave = async (data) => {
    try {
      dispatch(setLoading(true));
      if (editingEntry?._id) {
        await updateKnowledgeBase(editingEntry._id, data);
        toast.success('Property updated successfully');
      } else {
        await createKnowledgeBase(data);
        toast.success('Property created successfully');
      }
      setIsModalOpen(false);
      setEditingEntry(null);
      fetchEntries();
    } catch (err) {
      toast.error(err?.message || 'Failed to save property');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleDelete = async (entry) => {
    const result = await Swal.fire({
      title: 'Delete Property?',
      html: `<p>This will permanently remove <strong>${entry.property_name}</strong> from the knowledge base.</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, delete it',
    });
    if (!result.isConfirmed) return;
    try {
      dispatch(setLoading(true));
      await deleteKnowledgeBase(entry._id);
      toast.success('Property deleted');
      fetchEntries();
    } catch {
      toast.error('Failed to delete property');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleToggleActive = async (entry) => {
    try {
      dispatch(setLoading(true));
      await toggleKnowledgeBaseActive(entry._id);
      toast.success(`Property ${entry.is_active ? 'deactivated' : 'activated'}`);
      fetchEntries();
    } catch {
      toast.error('Failed to update status');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const filteredEntries = entries.filter(e => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      e.property_name?.toLowerCase().includes(q) ||
      e.city?.toLowerCase().includes(q) ||
      e.address?.toLowerCase().includes(q) ||
      e.slug?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="page-content kb-page">
      <MetaTags><title>Rentdigicare | Company Knowledge Base</title></MetaTags>
      <div className="container-fluid mb-2">
        <Breadcrumbs title="Settings" breadcrumbItem="Company Knowledge Base" />

        <KnowledgeBaseList
          entries={filteredEntries}
          searchQuery={searchQuery}
          filterCity={filterCity}
          filterType={filterType}
          filterActive={filterActive}
          onSearchChange={setSearchQuery}
          onFilterCityChange={setFilterCity}
          onFilterTypeChange={setFilterType}
          onFilterActiveChange={setFilterActive}
          onCreate={handleCreate}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleActive={handleToggleActive}
          userRole={user?.role || 'other'}
        />

        <KnowledgeBaseFormModal
          open={isModalOpen}
          entry={editingEntry}
          onClose={() => { setIsModalOpen(false); setEditingEntry(null); }}
          onSave={handleSave}
          userRole={user?.role || 'other'}
        />
      </div>
    </div>
  );
};

export { CompanyKnowledgeBase };